// Every backend endpoint the app calls, relative to appConfig.api.baseURL.
// Add new APIs here and call them through services/api.js, never with hard-coded URLs.
// IDs are always URL-encoded so a value can't change which endpoint is called.

const seg = (value) => {
  if (value === undefined || value === null || value === '') throw new Error('API path parameter is missing');
  return encodeURIComponent(String(value));
};

// Standard REST paths for a collection: list/create at `base`, item at `base/:id`.
const resource = (base) => ({
  list: base,
  create: base,
  byId: (id) => `${base}/${seg(id)}`,
  update: (id) => `${base}/${seg(id)}`,
  remove: (id) => `${base}/${seg(id)}`,
});

export const API = Object.freeze({
  auth: {
    login: '/auth/login',
    register: '/auth/register',
    logout: '/auth/logout',
    refresh: '/auth/refresh', // uses the httpOnly refresh-token cookie
    me: '/auth/me',
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/auth/reset-password',
    verifyEmail: '/auth/verify-email',
    resendVerification: '/auth/resend-verification',
    changePassword: '/auth/change-password',
  },

  profile: {
    me: '/profile',
    update: '/profile',
    avatar: '/profile/avatar',
    notificationPreferences: '/profile/notification-preferences',
  },

  public: {
    searchJourneys: '/public/journeys/search',
    page: (slug) => `/public/pages/${seg(slug)}`,
    faqs: '/public/faqs',
    prohibitedItems: '/public/prohibited-items',
    categories: '/public/categories',
    priceEstimate: '/public/price-estimate',
    contact: '/public/contact',
  },

  masterData: {
    countries: resource('/master/countries'),
    states: resource('/master/states'),
    cities: resource('/master/cities'),
    stations: resource('/master/stations'),
    transportModes: resource('/master/transport-modes'),
    categories: resource('/master/categories'),
    prohibitedItems: resource('/master/prohibited-items'),
  },

  sender: {
    dashboard: '/sender/dashboard',
    searchTravellers: '/sender/travellers/search',
    travellerProfile: (journeyId) => `/sender/journeys/${seg(journeyId)}/traveller`,
    requests: resource('/sender/requests'),
    cancelRequest: (id) => `/sender/requests/${seg(id)}/cancel`,
    assignment: (id) => `/sender/requests/${seg(id)}/assignment`,
    assignedTraveller: (id) => `/sender/requests/${seg(id)}/traveller`,
    handover: (id) => `/sender/requests/${seg(id)}/handover`,
    delivery: (id) => `/sender/requests/${seg(id)}/delivery`,
    createPayment: (id) => `/sender/requests/${seg(id)}/payments`,
    verifyPayment: (id) => `/sender/payments/${seg(id)}/verify`,
    payments: '/sender/payments',
    receipt: (paymentId) => `/sender/payments/${seg(paymentId)}/receipt`,
  },

  traveller: {
    dashboard: '/traveller/dashboard',
    journeys: resource('/traveller/journeys'),
    publishJourney: (id) => `/traveller/journeys/${seg(id)}/publish`,
    cancelJourney: (id) => `/traveller/journeys/${seg(id)}/cancel`,
    verification: '/traveller/verification',
    verificationDocuments: '/traveller/verification/documents',
    requests: resource('/traveller/requests'),
    confirmAssignment: (id) => `/traveller/requests/${seg(id)}/confirm`,
    declineAssignment: (id) => `/traveller/requests/${seg(id)}/decline`,
    senderDetails: (id) => `/traveller/requests/${seg(id)}/sender`,
    confirmHandover: (id) => `/traveller/requests/${seg(id)}/handover`,
    startTransit: (id) => `/traveller/requests/${seg(id)}/in-transit`,
    confirmDelivery: (id) => `/traveller/requests/${seg(id)}/delivery`,
    earnings: '/traveller/earnings',
  },

  // Shared by senders and travellers; the backend scopes results to the signed-in user.
  conversations: {
    list: '/conversations',
    byRequest: (requestId) => `/conversations/${seg(requestId)}`,
    messages: (requestId) => `/conversations/${seg(requestId)}/messages`,
  },
  notifications: {
    list: '/notifications',
    markRead: (id) => `/notifications/${seg(id)}/read`,
    markAllRead: '/notifications/read-all',
  },
  reviews: {
    list: '/reviews',
    pending: '/reviews/pending',
    create: '/reviews',
  },
  support: {
    tickets: resource('/support/tickets'),
  },

  admin: {
    dashboard: '/admin/dashboard',
    users: resource('/admin/users'),
    userStatus: (id) => `/admin/users/${seg(id)}/status`,
    senders: resource('/admin/senders'),
    travellers: resource('/admin/travellers'),
    verifications: resource('/admin/verifications'),
    verificationDecision: (travellerId) => `/admin/verifications/${seg(travellerId)}/decision`,
    journeys: resource('/admin/journeys'),
    requests: resource('/admin/requests'),
    assignmentCandidates: (requestId) => `/admin/requests/${seg(requestId)}/candidates`,
    assign: (requestId) => `/admin/requests/${seg(requestId)}/assign`,
    cancelRequest: (requestId) => `/admin/requests/${seg(requestId)}/cancel`,
    notifyParties: (requestId) => `/admin/requests/${seg(requestId)}/notify`,
    assignmentHistory: '/admin/assignments/history',
    payments: resource('/admin/payments'),
    cancellations: resource('/admin/cancellations'),
    cancellationDecision: (id) => `/admin/cancellations/${seg(id)}/decision`,
    refunds: resource('/admin/refunds'),
    processRefund: (id) => `/admin/refunds/${seg(id)}/process`,
    complaints: resource('/admin/complaints'),
    reviews: resource('/admin/reviews'),
    moderateReview: (id) => `/admin/reviews/${seg(id)}/moderation`,
    broadcasts: resource('/admin/notifications/broadcasts'),
    notificationTemplates: resource('/admin/notifications/templates'),
    pricing: resource('/admin/pricing-rules'),
    cmsPages: resource('/admin/cms/pages'),
    reports: {
      summary: '/admin/reports/summary',
      monthly: '/admin/reports/monthly',
      byMode: '/admin/reports/by-mode',
      topRoutes: '/admin/reports/top-routes',
      export: '/admin/reports/export',
    },
    admins: resource('/admin/admin-users'),
    settings: '/admin/settings',
  },
});

export default API;
